import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const storedValue = window.localStorage.getItem(key);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing. The in-memory state still works.
  }
};

const getItemId = (productOrId) =>
  typeof productOrId === "object" && productOrId !== null
    ? productOrId.id
    : productOrId;

const hasSameId = (firstId, secondId) => String(firstId) === String(secondId);

const isSameVariant = (item, productId, size, color) =>
  hasSameId(item.id, productId) &&
  (item.size || "") === (size || "") &&
  (item.color || "") === (color || "");

const normaliseQuantity = (quantity, fallback = 1) => {
  const numericQuantity = Number(quantity);
  return Number.isFinite(numericQuantity) && numericQuantity > 0
    ? Math.max(1, Math.floor(numericQuantity))
    : fallback;
};

const getStockLimit = (productId) => {
  const product = productData.find((item) => hasSameId(item.id, productId));
  const stock = Number(product?.stock);
  return Number.isFinite(stock) && stock >= 0 ? Math.floor(stock) : Infinity;
};

const capQuantityToStock = (productId, quantity) => {
  const stockLimit = getStockLimit(productId);
  if (stockLimit <= 0) return 0;
  return Math.min(normaliseQuantity(quantity), stockLimit);
};

export const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY)
      .filter(
        (item) =>
          item && typeof item === "object" && item.id !== undefined && item.id !== null
      )
      .map((item) => ({
        id: item.id,
        size: item.size || "",
        color: item.color || "",
        quantity: capQuantityToStock(item.id, item.quantity),
      }))
      .filter((item) => item.quantity > 0)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY)
      .map(getItemId)
      .filter((itemId) => itemId !== undefined && itemId !== null)
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback(
    (productOrId, quantity = 1, size = "", color = "") => {
      const productId = getItemId(productOrId);
      if (productId === undefined || productId === null) return;

      const quantityToAdd = capQuantityToStock(productId, quantity);
      if (quantityToAdd <= 0) return;
      const selectedSize = size || "";
      const selectedColor = color || "";

      setCartItems((currentItems) => {
        const itemExists = currentItems.some((item) =>
          isSameVariant(item, productId, selectedSize, selectedColor)
        );

        if (!itemExists) {
          return [
            ...currentItems,
            {
              id: productId,
              size: selectedSize,
              color: selectedColor,
              quantity: quantityToAdd,
            },
          ];
        }

        return currentItems.map((item) =>
          isSameVariant(item, productId, selectedSize, selectedColor)
            ? {
                ...item,
                quantity: capQuantityToStock(
                  productId,
                  normaliseQuantity(item.quantity) + quantityToAdd
                ),
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback((productOrId, size = "", color = "") => {
    const productId = getItemId(productOrId);
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) => !isSameVariant(item, productId, size, color)
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productOrId, size = "", color = "", quantity = 1) => {
      const productId = getItemId(productOrId);
      const nextQuantity = capQuantityToStock(productId, quantity);
      if (nextQuantity <= 0) return;

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          isSameVariant(item, productId, size, color)
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productOrId) => {
    const productId = getItemId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) =>
      currentItems.some((itemId) => hasSameId(itemId, productId))
        ? currentItems
        : [...currentItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = getItemId(productOrId);
    setWishlistItems((currentItems) =>
      currentItems.filter((itemId) => !hasSameId(itemId, productId))
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const productId = getItemId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) => {
      const isSaved = currentItems.some((itemId) =>
        hasSameId(itemId, productId)
      );
      return isSaved
        ? currentItems.filter((itemId) => !hasSameId(itemId, productId))
        : [...currentItems, productId];
    });
  }, []);

  const clearWishlist = useCallback(() => setWishlistItems([]), []);

  const isInWishlist = useCallback(
    (productOrId) => {
      const productId = getItemId(productOrId);
      return wishlistItems.some((itemId) => hasSameId(itemId, productId));
    },
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      wishlist: wishlistItems,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      isInWishlist,
      isWishlisted: isInWishlist,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      clearWishlist,
      isInWishlist,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
